/**
 * Expanded-body renderers for tool rows.
 *
 * The body sits flush under the inline-prose row (no surrounding
 * card chrome — see :class:`ToolUseBlockView`'s indented dropdown
 * region) and is gated by a tiny uppercase :class:`Section` header
 * that names the payload concretely:
 *
 *   - Bash → ``COMMAND`` (full unbounded command, since the row
 *     truncates anything past one line) + ``OUTPUT``.
 *   - Monitor → ``WATCHING`` (full bash one-liner) + ``STATUS``.
 *   - Read → ``FILE CONTENTS``.
 *   - Write → ``FILE`` + ``WRITTEN CONTENT``.
 *   - Edit → ``FILE`` + ``DIFF`` rendered as inline +/- lines.
 *   - MultiEdit → ``FILE`` + one ``EDIT N OF M`` block per edit.
 *   - NotebookEdit → ``FILE`` + ``NEW SOURCE``.
 *   - TodoWrite / TaskCreate / TaskUpdate / TaskGet / TaskList → custom checklist render (no Section).
 *   - ExitPlanMode → ``PLAN``.
 *   - ToolSearch → ``MATCHES`` (custom chip render).
 *   - Grep → ``MATCHES``, Glob → ``MATCHED FILES``,
 *     WebSearch → ``SEARCH RESULTS``, WebFetch → ``PAGE CONTENT``,
 *     Task / Agent → ``AGENT OUTPUT``, BashOutput → ``OUTPUT``,
 *     KillBash / KillShell / TaskStop → ``STATUS``, Skill / SkillTool /
 *     SlashCommand → ``OUTPUT``, anything else → ``OUTPUT``.
 *
 * Errors swap the label out for ``ERROR`` and color the body red.
 *
 * Inputs are NEVER re-echoed as raw JSON — the inline row already
 * tells the reader what the call is doing. The only exception is
 * the structured echo for tools whose payload IS the input
 * (``Edit`` / ``Write`` / ``TodoWrite`` / ``ExitPlanMode``).
 */

import { Check, X } from 'lucide-react';
import { normalizeTaskResultPayload } from '@/lib/agent/taskResults';
import type { ToolResultBlock, ToolUseBlock } from '@/lib/agent/types';
import { cn } from '@/lib/utils/cn';
import { DocumentBlockView } from './DocumentBlockView';
import {
  asString,
  extractToolReferences,
  hasTextualContent,
  type NormalizedMedia,
  normalizeTerminalOutput,
  toolResultText,
} from './helpers';
import { ImageBlockView } from './ImageBlockView';

const RESULT_LABEL_BY_TOOL_NAME: Record<string, string> = {
  Grep: 'MATCHES',
  Glob: 'MATCHED FILES',
  WebSearch: 'SEARCH RESULTS',
  WebFetch: 'PAGE CONTENT',
  Task: 'AGENT OUTPUT',
  Agent: 'AGENT OUTPUT',
  BashOutput: 'OUTPUT',
  KillBash: 'STATUS',
  KillShell: 'STATUS',
  TaskStop: 'STATUS',
  Skill: 'OUTPUT',
  SkillTool: 'OUTPUT',
  SlashCommand: 'OUTPUT',
};

/**
 * Compute the body element that should render under a tool row
 * when expanded. Returns ``null`` when there's nothing meaningful
 * to show — at which point the caller suppresses the expand
 * affordance entirely.
 */
export function renderToolBody({
  block,
  result,
  isError,
}: {
  block: ToolUseBlock;
  result?: ToolResultBlock;
  isError: boolean;
}): React.ReactNode | null {
  const input = block.input ?? {};
  const name = block.name;
  const isMediaOnly = !!result && !isError && !hasTextualContent(result);

  if (/^bash$/i.test(name)) {
    const command = asString(input.command) ?? asString(input.cmd) ?? '';
    if (!command && !result) return null;
    return (
      <div className="flex flex-col gap-2.5">
        {command ? (
          <Section label="COMMAND">
            <Mono>$ {command}</Mono>
          </Section>
        ) : null}
        <Section label={isError ? 'ERROR' : 'OUTPUT'}>
          {result ? (
            <ResultMono result={result} isError={isError} normalizeTerminal />
          ) : (
            <Mono muted>(awaiting output)</Mono>
          )}
        </Section>
      </div>
    );
  }

  if (name === 'Monitor') {
    const command = asString(input.command) ?? '';
    if (!command && !result) return null;
    return (
      <div className="flex flex-col gap-2.5">
        {command && (
          <Section label="WATCHING">
            <Mono>$ {command}</Mono>
          </Section>
        )}
        <Section label={isError ? 'ERROR' : 'STATUS'}>
          {result ? (
            <ResultMono result={result} isError={isError} normalizeTerminal />
          ) : (
            <Mono muted>(awaiting events)</Mono>
          )}
        </Section>
      </div>
    );
  }

  if (name === 'Edit' && typeof input.old_string === 'string' && typeof input.new_string === 'string') {
    const path = filePathForTool(input);
    const diff = <Diff old={String(input.old_string)} neu={String(input.new_string)} />;
    if (isError && result) {
      return (
        <div className="flex flex-col gap-2.5">
          <FilePathSection path={path} />
          <Section label="ERROR">
            <ResultMono result={result} isError />
          </Section>
          <Section label="ATTEMPTED DIFF">{diff}</Section>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2.5">
        <FilePathSection path={path} />
        <Section label="DIFF">{diff}</Section>
      </div>
    );
  }

  if (name === 'MultiEdit' && Array.isArray(input.edits)) {
    const path = filePathForTool(input);
    const edits = input.edits as Array<Record<string, unknown>>;
    const editSections = edits.map((edit, i) => {
      const oldStr = asString(edit?.old_string) ?? '';
      const newStr = asString(edit?.new_string) ?? '';
      return (
        <Section key={i} label={`EDIT ${i + 1} OF ${edits.length}`}>
          <Diff old={oldStr} neu={newStr} />
        </Section>
      );
    });
    if (isError && result) {
      return (
        <div className="flex flex-col gap-2.5">
          <FilePathSection path={path} />
          <Section label="ERROR">
            <ResultMono result={result} isError />
          </Section>
          {editSections}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        <FilePathSection path={path} />
        {editSections}
      </div>
    );
  }

  if (name === 'NotebookEdit') {
    const path = filePathForTool(input);
    const source = asString(input.new_source) ?? '';
    if (isError && result) {
      return (
        <div className="flex flex-col gap-2.5">
          <FilePathSection path={path} />
          <Section label="ERROR">
            <ResultMono result={result} isError />
          </Section>
          <Section label="ATTEMPTED SOURCE">{source ? <Mono>{source}</Mono> : <Mono muted>(empty cell)</Mono>}</Section>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2.5">
        <FilePathSection path={path} />
        <Section label="NEW SOURCE">{source ? <Mono>{source}</Mono> : <Mono muted>(empty cell)</Mono>}</Section>
      </div>
    );
  }

  if (name === 'Write') {
    const path = filePathForTool(input);
    const content = asString(input.content) ?? '';
    const attempted =
      content.length > 0 ? (
        <Mono>{content}</Mono>
      ) : isError ? (
        <Mono muted>(interrupted before content was available)</Mono>
      ) : !result ? (
        <Mono muted>(awaiting content)</Mono>
      ) : (
        <Mono muted>(empty file)</Mono>
      );
    if (isError && result) {
      return (
        <div className="flex flex-col gap-2.5">
          <FilePathSection path={path} />
          <Section label="ERROR">
            <ResultMono result={result} isError />
          </Section>
          <Section label="ATTEMPTED CONTENT">{attempted}</Section>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2.5">
        <FilePathSection path={path} />
        <Section label="WRITTEN CONTENT">{attempted}</Section>
      </div>
    );
  }

  if (name === 'Read') {
    if (isMediaOnly) return null;
    return (
      <Section label={isError ? 'ERROR' : 'FILE CONTENTS'}>
        {result ? <ResultMono result={result} isError={isError} /> : <Mono muted>(awaiting file slice)</Mono>}
      </Section>
    );
  }

  if (name === 'TodoWrite') {
    const todos = Array.isArray(input.todos) ? (input.todos as Array<Record<string, unknown>>) : [];
    if (isError && result) {
      return (
        <div className="flex flex-col gap-2.5">
          <Section label="ERROR">
            <ResultMono result={result} isError />
          </Section>
          <Section label="ATTEMPTED TODOS">
            {todos.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {todos.map((todo, i) => {
                  const status = asString(todo?.status) ?? 'pending';
                  const content = asString(todo?.content) ?? '';
                  return <TodoRow key={i} status={status} content={content} />;
                })}
              </ul>
            ) : (
              <span className="text-[12px] text-gray-500 italic dark:text-gray-400">No todos</span>
            )}
          </Section>
        </div>
      );
    }
    if (todos.length === 0) {
      return <span className="text-[12px] text-gray-500 italic dark:text-gray-400">No todos</span>;
    }
    return (
      <ul className="flex flex-col gap-1.5">
        {todos.map((todo, i) => {
          const status = asString(todo?.status) ?? 'pending';
          const content = asString(todo?.content) ?? '';
          return <TodoRow key={i} status={status} content={content} />;
        })}
      </ul>
    );
  }

  if (isTaskListTool(name)) {
    return renderTaskToolBody({ name, input, result, isError });
  }

  if (name === 'ExitPlanMode') {
    const plan = asString(input.plan) ?? '';
    return <Section label="PLAN">{plan ? <Mono>{plan}</Mono> : <Mono muted>(empty plan)</Mono>}</Section>;
  }

  if (name === 'ToolSearch') {
    if (!result) return <Mono muted>(awaiting result)</Mono>;
    if (isError) {
      return (
        <Section label="ERROR">
          <ResultMono result={result} isError />
        </Section>
      );
    }
    const tools = extractToolReferences(result);
    if (tools.length === 0)
      return <span className="text-[12px] text-gray-500 italic dark:text-gray-400">No matching tools</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {tools.map((tool, i) => (
          <span
            key={i}
            className={cn(
              'inline-flex items-center rounded-md border bg-white px-2 py-0.5 font-mono text-[11.5px]',
              'border-gray-200/80 text-gray-700',
              'dark:border-white/15 dark:bg-white/5 dark:text-gray-200'
            )}
          >
            {tool}
          </span>
        ))}
      </div>
    );
  }

  const terminalLikeResult = name === 'Task' || name === 'Agent' || name === 'BashOutput';
  if (name in RESULT_LABEL_BY_TOOL_NAME) {
    if (isMediaOnly) return null;
    return (
      <Section label={isError ? 'ERROR' : RESULT_LABEL_BY_TOOL_NAME[name]}>
        {result ? (
          <ResultMono result={result} isError={isError} normalizeTerminal={terminalLikeResult} />
        ) : (
          <Mono muted>(awaiting result)</Mono>
        )}
      </Section>
    );
  }

  if (isMediaOnly) return null;
  return (
    <Section label={isError ? 'ERROR' : 'OUTPUT'}>
      {result ? <ResultMono result={result} isError={isError} /> : <Mono muted>(awaiting result)</Mono>}
    </Section>
  );
}

/**
 * Cheap predicate for whether :func:`renderToolBody` would produce
 * visible content. Keep this free of React element construction so
 * collapsed tool rows do not materialize large outputs in memory.
 */
export function hasToolBody({
  block,
  result,
  isError,
}: {
  block: ToolUseBlock;
  result?: ToolResultBlock;
  isError: boolean;
}): boolean {
  const input = block.input ?? {};
  const name = block.name;
  const isMediaOnly = !!result && !isError && !hasTextualContent(result);

  if (/^bash$/i.test(name)) {
    const command = asString(input.command) ?? asString(input.cmd) ?? '';
    return !!command || !!result;
  }

  if (name === 'Monitor') {
    const command = asString(input.command) ?? '';
    return !!command || !!result;
  }

  if (name === 'Edit') {
    return typeof input.old_string === 'string' && typeof input.new_string === 'string';
  }

  if (name === 'MultiEdit') {
    return Array.isArray(input.edits);
  }

  if (name === 'NotebookEdit') {
    return true;
  }

  if (name === 'Write') {
    return true;
  }

  if (name === 'Read') {
    return !isMediaOnly;
  }

  if (name === 'TodoWrite' || isTaskListTool(name) || name === 'ExitPlanMode' || name === 'ToolSearch') {
    return true;
  }

  if (name in RESULT_LABEL_BY_TOOL_NAME) {
    return !isMediaOnly;
  }
  return !isMediaOnly;
}

const TASK_LIST_TOOLS = new Set(['TaskCreate', 'TaskUpdate', 'TaskGet', 'TaskList']);

function isTaskListTool(name: string): boolean {
  return TASK_LIST_TOOLS.has(name);
}

function renderTaskToolBody({
  name,
  input,
  result,
  isError,
}: {
  name: string;
  input: Record<string, unknown>;
  result?: ToolResultBlock;
  isError: boolean;
}): React.ReactNode {
  if (isError && result) {
    return (
      <div className="flex flex-col gap-2.5">
        <Section label="ERROR">
          <ResultMono result={result} isError />
        </Section>
        <Section label="ATTEMPTED TASK">
          <TaskRows tasks={tasksFromTaskToolInput(input)} emptyLabel="No task fields" />
        </Section>
      </div>
    );
  }

  const payload = result ? taskPayloadFromResult(result, name) : null;
  if (name === 'TaskList') {
    if (!result) return <Mono muted>(awaiting result)</Mono>;
    const tasks = tasksFromTaskListPayload(payload);
    // Fail safe on wire-format drift: only assert the empty state when
    // the result IS the real empty-list text. Asserting "No tasks" over
    // an unparsed non-empty result would be affirmative misinformation
    // — show the raw text instead so nothing is silently dropped.
    if (tasks.length === 0 && !isEmptyTaskListText(result)) {
      return <ResultMono result={result} isError={false} />;
    }
    return <TaskRows tasks={tasks} emptyLabel="No tasks" />;
  }
  if (name === 'TaskGet') {
    if (!result) return <Mono muted>(awaiting result)</Mono>;
    const tasks = tasksFromTaskGetPayload(payload);
    // Same drift guard as TaskList: an unparsable TaskGet result is
    // shown raw rather than misreported as "Task not found".
    if (tasks.length === 0 && toolResultText(result)) {
      return <ResultMono result={result} isError={false} />;
    }
    return <TaskRows tasks={tasks} emptyLabel="Task not found" />;
  }
  if (name === 'TaskCreate') {
    const tasks = tasksFromTaskCreatePayload(payload, input);
    return <TaskRows tasks={tasks.length > 0 ? tasks : tasksFromTaskToolInput(input)} emptyLabel="No task" />;
  }
  // TaskUpdate: the result text carries only the id + changed field
  // *names* (no subject), so render the change from the call input —
  // a real subject when the update set one, else ``Task #<id>`` — with
  // the concrete changes as the meta line.
  return <TaskRows tasks={tasksFromTaskUpdate(input, payload)} emptyLabel="No task fields" />;
}

interface RenderedTask {
  subject: string;
  status: string;
  description?: string;
  meta?: string;
}

function TaskRows({ tasks, emptyLabel }: { tasks: RenderedTask[]; emptyLabel: string }) {
  if (tasks.length === 0) {
    return <span className="text-[12px] text-gray-500 italic dark:text-gray-400">{emptyLabel}</span>;
  }
  return (
    <ul className="flex flex-col gap-2">
      {tasks.map((task, i) => (
        <TaskRow key={`${i}-${task.subject}`} task={task} />
      ))}
    </ul>
  );
}

function TaskRow({ task }: { task: RenderedTask }) {
  // Dot + subject share one ``items-center`` row so the status marker
  // sits centered on the title line; the description / meta wrap below,
  // indented to the subject's left edge (icon ``size-3.5`` + ``gap-2``
  // = 22px). ``select-text`` keeps the title and detail copyable.
  return (
    <li className="flex select-text flex-col gap-0.5 text-[12.5px] leading-snug">
      <span className="flex min-w-0 items-center gap-2">
        <TodoStatusIcon status={task.status} />
        <span className={cn('min-w-0 break-words', taskTextClass(task.status))}>{task.subject}</span>
      </span>
      {task.description && (
        <span className="break-words pl-[22px] text-[11px] text-gray-500 leading-snug dark:text-gray-400">
          {task.description}
        </span>
      )}
      {task.meta && (
        <span className="truncate pl-[22px] text-[10.5px] text-gray-400 dark:text-gray-500">{task.meta}</span>
      )}
    </li>
  );
}

function taskTextClass(status: string): string {
  if (status === 'completed') return 'line-through text-gray-500 dark:text-gray-400';
  if (status === 'deleted' || status === 'cancelled' || status === 'canceled') {
    return 'line-through text-gray-400 dark:text-gray-500';
  }
  if (status === 'in_progress') return 'font-medium text-gray-900 dark:text-gray-50';
  return 'text-gray-700 dark:text-gray-200';
}

function tasksFromTaskToolInput(input: Record<string, unknown>): RenderedTask[] {
  const subject = asString(input.subject) ?? (asString(input.taskId) ? `Task #${asString(input.taskId)}` : '');
  if (!subject) return [];
  const status = asString(input.status) ?? 'pending';
  return [{ subject, status, description: asString(input.description), meta: taskMeta(input) }];
}

/**
 * Build the body row for a ``TaskUpdate``. The result text gives only
 * the id + changed field names, so the subject and concrete new values
 * come from the call ``input``: a real subject when the update renamed
 * the task, else ``Task #<id>``, with the actual changes (status,
 * owner, dependencies) summarized on the meta line.
 */
function tasksFromTaskUpdate(input: Record<string, unknown>, payload: Record<string, unknown> | null): RenderedTask[] {
  const taskId = asString(input.taskId) ?? asString(payload?.taskId);
  if (!taskId) return [];
  const status = asString(input.status) ?? 'pending';
  const subject = asString(input.subject) ?? `Task #${taskId}`;
  const changes = taskUpdateChanges(input);
  return [
    {
      subject,
      status,
      description: asString(input.description),
      meta: changes.length > 0 ? `#${taskId} · ${changes.join(' · ')}` : `#${taskId}`,
    },
  ];
}

/** Human-readable summary of the fields a ``TaskUpdate`` changed. */
function taskUpdateChanges(input: Record<string, unknown>): string[] {
  const parts: string[] = [];
  const status = asString(input.status);
  if (status) parts.push(`status → ${status}`);
  const owner = asString(input.owner);
  if (owner) parts.push(`owner → ${owner}`);
  if (asString(input.subject)) parts.push('renamed');
  if (asString(input.description)) parts.push('description updated');
  if (asString(input.activeForm)) parts.push('active form updated');
  const addBlockedBy = stringArray(input.addBlockedBy);
  if (addBlockedBy.length > 0) parts.push(`blocked by ${addBlockedBy.map((id) => `#${id}`).join(', ')}`);
  const addBlocks = stringArray(input.addBlocks);
  if (addBlocks.length > 0) parts.push(`blocks ${addBlocks.map((id) => `#${id}`).join(', ')}`);
  return parts;
}

function tasksFromTaskCreatePayload(
  payload: Record<string, unknown> | null,
  input: Record<string, unknown>
): RenderedTask[] {
  const raw = payload?.task;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const task = raw as Record<string, unknown>;
  const subject = asString(task.subject) ?? asString(input.subject) ?? '';
  if (!subject) return [];
  // ``description`` only ever rides the call input (the create result
  // text is just id + subject), so merge input under the parsed task.
  return [renderedTaskFromRecord({ ...input, ...task }) ?? { subject, status: asString(task.status) ?? 'pending' }];
}

function tasksFromTaskGetPayload(payload: Record<string, unknown> | null): RenderedTask[] {
  const raw = payload?.task;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const task = renderedTaskFromRecord(raw as Record<string, unknown>);
  return task ? [task] : [];
}

function tasksFromTaskListPayload(payload: Record<string, unknown> | null): RenderedTask[] {
  const rawTasks = payload?.tasks;
  if (!Array.isArray(rawTasks)) return [];
  return rawTasks
    .map((raw) =>
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? renderedTaskFromRecord(raw as Record<string, unknown>)
        : null
    )
    .filter((task): task is RenderedTask => task !== null);
}

function renderedTaskFromRecord(task: Record<string, unknown>): RenderedTask | null {
  const id = asString(task.id) ?? asString(task.taskId);
  const subject = asString(task.subject) ?? (id ? `Task #${id}` : '');
  if (!subject) return null;
  return {
    subject,
    status: asString(task.status) ?? 'pending',
    description: asString(task.description),
    meta: taskMeta(task),
  };
}

function taskMeta(task: Record<string, unknown>): string | undefined {
  const parts: string[] = [];
  const id = asString(task.id) ?? asString(task.taskId);
  const owner = asString(task.owner);
  const blockedBy = stringArray(task.blockedBy);
  const blocks = stringArray(task.blocks);
  if (id) parts.push(`#${id}`);
  if (owner) parts.push(owner);
  if (blockedBy.length > 0) parts.push(`blocked by ${blockedBy.map((blockerId) => `#${blockerId}`).join(', ')}`);
  if (blocks.length > 0) parts.push(`${blocks.length} ${blocks.length === 1 ? 'block' : 'blocks'}`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : [];
}

/**
 * Normalize a Task-tool result for the body renderers — thin wrapper
 * over the shared :func:`normalizeTaskResultPayload` policy.
 */
function taskPayloadFromResult(result: ToolResultBlock, name: string): Record<string, unknown> | null {
  return normalizeTaskResultPayload(name, result.content, toolResultText(result));
}

/** Whether a TaskList result is the genuine empty-list text. */
function isEmptyTaskListText(result: ToolResultBlock): boolean {
  const text = toolResultText(result);
  return text !== null && /^no tasks found\.?$/i.test(text.trim());
}

/**
 * Wrap a payload pane (bash output, file contents, diff, …) with a
 * tiny uppercase header. Keeps every expanded tool body framed the
 * same way so the user always knows what they're looking at.
 */
function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium text-[9.5px] text-gray-500 uppercase tracking-[0.08em] dark:text-gray-400">
        {label}
      </span>
      {children}
    </div>
  );
}

function FilePathSection({ path }: { path: string | undefined }) {
  if (!path) return null;
  return (
    <Section label="FILE">
      <Mono>{path}</Mono>
    </Section>
  );
}

export function filePathForTool(input: Record<string, unknown>): string | undefined {
  return (
    asString(input.file_path) ??
    asString(input.notebook_path) ??
    asString(input.target_notebook) ??
    asString(input.path)
  );
}

/**
 * Monospace pane used for tool inputs / results. Uses a soft tinted
 * background that reads on both light and dark surfaces — distinctly
 * warmer than the page, but not the jarring white-on-grey. No
 * border: the tint alone carries the box, so the dropdown stays
 * chrome-light.
 */
function Mono({ children, muted, isError }: { children: React.ReactNode; muted?: boolean; isError?: boolean }) {
  return (
    <pre
      className={cn(
        'agent-scroll max-h-64 overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-all rounded-md',
        'bg-gray-100/80 px-3 py-2 font-mono text-[11.5px]',
        'dark:bg-white/[0.04]',
        isError
          ? 'text-red-700 dark:text-red-300'
          : muted
            ? 'text-gray-500 dark:text-gray-400'
            : 'text-gray-800 dark:text-gray-100'
      )}
    >
      {children}
    </pre>
  );
}

function Diff({ old, neu }: { old: string; neu: string }) {
  return (
    <pre
      className={cn(
        'agent-scroll max-h-72 overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words rounded-md',
        'bg-gray-100/80 px-3 py-2 font-mono text-[12.5px] leading-5',
        'dark:bg-white/[0.04]'
      )}
    >
      {old.split('\n').map((l, i) => (
        <div key={`o${i}`} className="font-medium text-red-700 dark:text-red-300">
          <span className="select-none opacity-60">- </span>
          {l}
        </div>
      ))}
      {neu.split('\n').map((l, i) => (
        <div key={`n${i}`} className="font-medium text-emerald-700 dark:text-emerald-300">
          <span className="select-none opacity-60">+ </span>
          {l}
        </div>
      ))}
    </pre>
  );
}

/**
 * Render a :type:`ToolResultBlock`'s payload directly (no header).
 * Errors paint the body red; non-text content (images, documents)
 * already surfaced via the always-visible media row above.
 */
function ResultMono({
  result,
  isError,
  normalizeTerminal,
}: {
  result: ToolResultBlock;
  isError: boolean;
  normalizeTerminal?: boolean;
}) {
  const text = toolResultText(result);
  if (text === null) return null;
  return (
    <Mono isError={isError} muted={!isError}>
      {normalizeTerminal ? normalizeTerminalOutput(text) : text}
    </Mono>
  );
}

/**
 * One row in the TodoWrite body — a checkbox-style status indicator
 * followed by the todo text.
 */
function TodoRow({ status, content }: { status: string; content: string }) {
  const textClass =
    status === 'completed'
      ? 'line-through text-gray-500 dark:text-gray-400'
      : status === 'cancelled'
        ? 'line-through text-gray-400 dark:text-gray-500'
        : status === 'in_progress'
          ? 'font-medium text-gray-900 dark:text-gray-50'
          : 'text-gray-700 dark:text-gray-200';
  return (
    <li className="flex items-start gap-2 text-[12.5px] leading-snug">
      <TodoStatusIcon status={status} className="mt-[3px]" />
      <span className={cn('break-words', textClass)}>{content}</span>
    </li>
  );
}

function TodoStatusIcon({ status, className }: { status: string; className?: string }) {
  const base = cn('flex size-3.5 shrink-0 items-center justify-center rounded-full border', className);
  if (status === 'completed') {
    return (
      <span
        className={cn(
          base,
          'border-emerald-500 bg-emerald-500 text-white',
          'dark:border-emerald-400 dark:bg-emerald-400 dark:text-black'
        )}
      >
        <Check className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span
        className={cn(
          base,
          'border-sky-500 bg-sky-500/15 text-sky-600',
          'dark:border-sky-400 dark:bg-sky-400/20 dark:text-sky-300'
        )}
      >
        <span className="size-1.5 rounded-full bg-sky-500 dark:bg-sky-300" />
      </span>
    );
  }
  if (status === 'cancelled' || status === 'deleted') {
    return (
      <span
        className={cn(base, 'border-gray-300 bg-transparent text-gray-400 dark:border-white/15 dark:text-gray-500')}
      >
        <X className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  return <span className={cn(base, 'border-gray-300 bg-transparent dark:border-white/20')} />;
}

/** Image / document thumbnail rendered above the expanded body. */
export function MediaThumb({
  piece,
  preferredPathCandidates,
}: {
  piece: NormalizedMedia;
  preferredPathCandidates?: string[];
}) {
  if (piece.kind === 'image') {
    return <ImageBlockView block={piece.block} dense inlinePreview preferredPathCandidates={preferredPathCandidates} />;
  }
  return <DocumentBlockView block={piece.block} />;
}
